import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _debounce from 'lodash/debounce';
import PhoneInput, { CountryData, PhoneInputProps } from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { Box, IconButton, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { styled } from '@mui/system';

const Root = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
}));

type PhoneInputStyledProps = PhoneInputProps & {
    valid?: boolean;
    disabled?: boolean;
};

const PhoneInputStyled = styled(PhoneInput)<PhoneInputStyledProps>(
    ({ theme, valid, disabled }) => ({
        marginBottom: theme.spacing(2),
        height: '3.5rem',
        width: '100%',
        '&.react-tel-input .form-control': {
            height: '3.25rem',
            width: '100%',
            color: disabled
                ? theme.palette.text.disabled
                : theme.palette.primary.main,
            paddingTop: '12px',
            paddingBottom: '8px',
            borderColor: disabled
                ? theme.palette.action.disabled
                : theme.palette.border.light,
            borderRadius: '10px',
            [theme.breakpoints.up('md')]: {
                fontSize: '14px',
            },
            '&:hover': {
                borderColor: disabled
                    ? theme.palette.action.disabled
                    : theme.palette.border.main,
            },
            '&:focus': {
                borderColor: disabled
                    ? theme.palette.action.disabled
                    : theme.palette.border.main,
                boxShadow: 'none',
            },
        },
        '&.react-tel-input .special-label': {
            color: disabled
                ? theme.palette.text.disabled
                : theme.palette.primary.main,
            left: '10px',
            fontSize: '10px',
            backgroundColor: 'white',
        },
        '&.react-tel-input .selected-flag .arrow': {
            borderTopColor: disabled
                ? theme.palette.text.disabled
                : theme.palette.primary.main,
            borderLeftWidth: '4px',
            borderRightWidth: '4px',
            marginTop: 0,
        },
        '&.react-tel-input .selected-flag:focus .arrow': {
            borderTopColor: disabled
                ? theme.palette.text.disabled
                : theme.palette.primary.main,
        },
        '&.react-tel-input .selected-flag .arrow.up': {
            borderBottomColor: disabled
                ? theme.palette.text.disabled
                : theme.palette.primary.main,
        },
        '&.react-tel-input .country-list': {
            color: theme.palette.primary.main,
            '& .country .dial-code': {
                color: theme.palette.primary.main,
            },
        },
    }),
);

const CheckIconStyled = styled(CheckIcon)(({ theme }) => ({
    position: 'absolute',
    right: theme.spacing(2),
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.primary.light,
    pointerEvents: 'none',
}));

const CopyButtonStyled = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    right: theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.primary.main,
    padding: '6px',
}));

const PhoneInputWrapper = styled(Box)(({ theme }) => ({
    flex: 1,
    height: '3rem',
    width: '100%',
    position: 'relative',
}));

interface EnhancedPhoneNumberComponentProps {
    label: string;
    initialValue?: string;
    onChange: (value: string) => void;
    valid?: boolean;
    autoComplete?: string;
    required?: boolean;
    enabled?: boolean;
}

const PhoneNumberComponent = (props: EnhancedPhoneNumberComponentProps) => {
    const { t } = useTranslation();
    const [value, setValue] = useState<string>('');
    const [copied, setCopied] = useState<boolean>(false);
    const enabled = props.enabled !== false; // Default to true if not specified

    const handleCopy = async () => {
        const number = value.startsWith('+') ? value : `+${value}`;
        try {
            await navigator.clipboard.writeText(number);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard API unavailable (e.g. insecure context) — fail silently
        }
    };

    const normalizeValue = () => {
        if (value.startsWith('490')) {
            setValue('49' + value.slice(3));
        }
    };

    const debouncedOnChange = useCallback(
        _debounce(props.onChange, 700, { leading: false, trailing: true }),
        [],
    );

    useEffect(() => {
        if (props.initialValue) {
            setValue(props.initialValue);
        }
    }, [props.initialValue]);

    const handleChange = (newValue: string, countryData: CountryData) => {
        if (!enabled) return;

        if (newValue.length >= countryData.dialCode.length) {
            setValue(newValue);
            debouncedOnChange(newValue);
        } else {
            setValue(countryData.dialCode);
            debouncedOnChange(countryData.dialCode);
        }
    };

    useEffect(() => {
        setValue(props.initialValue ?? '');
    }, [props.initialValue]);

    return (
        <Root className={'relative'}>
            <PhoneInputWrapper>
                <PhoneInputStyled
                    country={'de'}
                    value={value}
                    onChange={handleChange}
                    onBlur={normalizeValue}
                    specialLabel={props.label}
                    countryCodeEditable={value === '' && enabled}
                    valid={props.valid}
                    disabled={!enabled}
                    inputProps={{
                        disabled: !enabled,
                        style: {
                            backgroundColor: 'white', // Force white background even when disabled
                        },
                    }}
                />
                {props.valid && enabled && <CheckIconStyled />}
                {!enabled && value && (
                    <Tooltip title={copied ? t('common.copied') : t('common.copy')}>
                        <CopyButtonStyled
                            onClick={handleCopy}
                            aria-label={t('common.copy')}
                            size={'small'}
                        >
                            {copied ? (
                                <CheckIcon fontSize={'small'} />
                            ) : (
                                <ContentCopyIcon fontSize={'small'} />
                            )}
                        </CopyButtonStyled>
                    </Tooltip>
                )}
            </PhoneInputWrapper>
        </Root>
    );
};

export default PhoneNumberComponent;
